import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CalendarDays, CheckCircle2, Download, Pencil, Receipt, Search, UserRound } from "lucide-react";
import { EmptyState } from "./common";
import { fmtMobile } from "./MobileVerifyGate";
import { PaginationBar, TableRowsSkeleton } from "./PaginationBar";
import { FetchButton, FetchDirtyHint, FetchForm, FetchPrompt, useFetchGate } from "./FetchBar";
import { EMPTY_TERRITORY, TerritoryFilterSelects, territoryParams, useTerritoryMasters } from "./TerritoryFilter";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { api, downloadBlob, errMsg, fmtDateTime, istDateISO } from "../lib/api";
import { cn } from "../lib/utils";

/**
 * Phase 204 — Mobile Number Change Log.
 * Every "Update number" and "Number is correct" event recorded by Tax Collectors / Supervisors on the property page
 * (Phase 134), with the receipt of a payment the same staff member collected on that property in the same visit
 * (±30 minutes). Shared by Municipal Admin → Reports and Super Admin → Mobile Numbers → Change Log; both call the
 * tenant endpoint `/t/{code}/reports/mobile-change-log` (Super Admin tokens are accepted on tenant routes).
 */
const ROLE_OPTIONS = [["all", "All roles"], ["collector", "Field Collector"], ["supervisor", "Supervisor"]];
const EVENT_OPTIONS = [["all", "All events"], ["updated", "Updated"], ["verified", "Verified"]];
const PRESETS = [["Today", 0], ["7 days", 6], ["30 days", 29]];
const daysAgoIST = (n) => istDateISO(new Date(Date.now() - n * 86400000));

export const EventBadge = ({ type, testId }) => (
  type === "updated" ? (
    <Badge variant="outline" className="gap-1 bg-primary/10 text-primary border-primary/20" data-testid={testId}>
      <Pencil className="h-3 w-3" aria-hidden="true" /> Updated
    </Badge>
  ) : (
    <Badge variant="outline" className="gap-1 bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border-[hsl(var(--success))]/20" data-testid={testId}>
      <CheckCircle2 className="h-3 w-3" aria-hidden="true" /> Verified
    </Badge>
  )
);

export function MobileChangeLogReport({ code, testId = "mcl", propertyPath = null, className = "" }) {
  const [dateFrom, setDateFrom] = useState(daysAgoIST(29));
  const [dateTo, setDateTo] = useState(istDateISO());
  const [area, setArea] = useState(EMPTY_TERRITORY);
  const [changedBy, setChangedBy] = useState("");
  const [role, setRole] = useState("all");
  const [eventType, setEventType] = useState("all");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const masters = useTerritoryMasters(code);

  // reset everything when the municipality changes (Super Admin tab)
  useEffect(() => { setData(null); setError(null); setPage(1); setArea(EMPTY_TERRITORY); }, [code]);

  const filters = useMemo(() => ({ dateFrom, dateTo, zoneId: area.zoneId, wardId: area.wardId, changedBy: changedBy.trim(), role, eventType }),
    [dateFrom, dateTo, area.zoneId, area.wardId, changedBy, role, eventType]);
  const gate = useFetchGate(filters);
  const a = gate.applied;
  useEffect(() => { setPage(1); }, [gate.appliedKey]);

  const params = useCallback((f) => ({
    date_from: f.dateFrom || undefined, date_to: f.dateTo || undefined,
    ...territoryParams({ zoneId: f.zoneId, wardId: f.wardId }),
    changed_by: f.changedBy || undefined, role: f.role, event_type: f.eventType,
  }), []);

  const load = useCallback(() => {
    if (!a || !code) return;
    setLoading(true); setError(null);
    api.get(`/t/${code}/reports/mobile-change-log`, { params: { ...params(a), page, limit } })
      .then((r) => setData(r.data))
      .catch((e) => { setError(errMsg(e)); setData(null); })
      .finally(() => setLoading(false));
  }, [code, a, params, page, limit]);
  useEffect(() => { if (gate.fetched) load(); }, [load, gate.fetched, gate.version]);

  const doExport = async () => {
    if (!a) return;
    setExporting(true);
    try {
      const q = Object.fromEntries(Object.entries(params(a)).filter(([, v]) => v !== undefined && v !== ""));
      await downloadBlob(`/t/${code}/reports/mobile-change-log/export?${new URLSearchParams(q).toString()}`,
        `mobile-number-change-log_${a.dateFrom || "all"}_to_${a.dateTo || "all"}.xlsx`);
      toast.success("Excel downloaded");
    } catch (e) { toast.error(errMsg(e)); } finally { setExporting(false); }
  };

  const setRange = (from, to) => { setDateFrom(from); setDateTo(to); };
  const activePreset = PRESETS.find(([, n]) => dateFrom === daysAgoIST(n) && dateTo === istDateISO())?.[0];
  const items = data?.items || [];
  const summary = data?.summary || {};
  const hasArea = !!(area.zoneId || area.wardId);

  return (
    <div className={cn("space-y-4", className)} data-testid={`${testId}-report`}>
      {/* filters */}
      <FetchForm onFetch={gate.fetch} className="flex flex-wrap items-center gap-3" testId={`${testId}-filter-form`}>
        <div className="flex items-center gap-1.5" data-testid={`${testId}-date-range`}>
          <CalendarDays className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <Input type="date" value={dateFrom} className="w-40" aria-label="From date" max={dateTo || undefined}
            onChange={(e) => setRange(e.target.value, dateTo)} data-testid={`${testId}-date-from`} />
          <span className="text-xs text-muted-foreground">to</span>
          <Input type="date" value={dateTo} className="w-40" aria-label="To date" min={dateFrom || undefined}
            onChange={(e) => setRange(dateFrom, e.target.value)} data-testid={`${testId}-date-to`} />
        </div>
        <div className="inline-flex rounded-md border bg-muted/40 p-0.5" role="group" aria-label="Quick ranges" data-testid={`${testId}-date-presets`}>
          {PRESETS.map(([label, n]) => (
            <Button key={label} type="button" size="sm" variant={activePreset === label ? "default" : "ghost"} className="h-7 px-2.5 text-xs"
              onClick={() => setRange(daysAgoIST(n), istDateISO())} data-testid={`${testId}-preset-${label.replace(/\s+/g, "-").toLowerCase()}`}>
              {label}
            </Button>
          ))}
        </div>
        <div className="w-full lg:w-[420px]">
          <TerritoryFilterSelects masters={masters} value={area} onChange={setArea} levels={["zone", "ward"]} testIdPrefix={testId} />
        </div>
        <div className="relative">
          <UserRound className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <Input className="pl-9 w-56" placeholder="Changed by — name or ID" value={changedBy} aria-label="Changed by name or employee ID"
            onChange={(e) => setChangedBy(e.target.value)} data-testid={`${testId}-changed-by`} />
        </div>
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger className="w-44" aria-label="Role" data-testid={`${testId}-role-select`}><SelectValue /></SelectTrigger>
          <SelectContent>
            {ROLE_OPTIONS.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`${testId}-role-${v}`}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={eventType} onValueChange={setEventType}>
          <SelectTrigger className="w-40" aria-label="Event type" data-testid={`${testId}-event-select`}><SelectValue /></SelectTrigger>
          <SelectContent>
            {EVENT_OPTIONS.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`${testId}-event-${v}`}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        {(hasArea || changedBy || role !== "all" || eventType !== "all") && (
          <Button type="button" variant="ghost" size="sm" className="text-muted-foreground"
            onClick={() => { setArea(EMPTY_TERRITORY); setChangedBy(""); setRole("all"); setEventType("all"); }} data-testid={`${testId}-clear-filters`}>
            Clear filters
          </Button>
        )}
        <FetchButton type="submit" size="sm" loading={loading} dirty={gate.dirty} fetched={gate.fetched} disabled={!code} testId={`${testId}-fetch-button`} />
        <FetchDirtyHint dirty={gate.dirty} testId={`${testId}-fetch-dirty`} />
        <Button type="button" variant="outline" size="sm" className="h-10 ml-auto" onClick={doExport}
          disabled={!gate.fetched || exporting || loading || !data || data.total === 0} data-testid={`${testId}-export-button`}>
          <Download className="h-4 w-4 mr-1.5" aria-hidden="true" /> {exporting ? "Preparing…" : "Export to Excel"}
        </Button>
      </FetchForm>

      {/* summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid={`${testId}-summary`}>
        {[["Total events", summary.total, `${testId}-stat-total`, null],
          ["Numbers updated", summary.updated, `${testId}-stat-updated`, Pencil],
          ["Numbers verified", summary.verified, `${testId}-stat-verified`, CheckCircle2],
          ["With receipt", summary.with_receipt, `${testId}-stat-receipt`, Receipt]].map(([l, v, tid, Icon]) => (
          <Card key={l} className="shadow-sm">
            <CardContent className="p-3.5 flex items-start justify-between gap-2">
              <div>
                <p className="text-[11px] text-muted-foreground">{l}</p>
                <p className="text-xl font-semibold tabular-nums" data-testid={tid}>{gate.fetched && data ? (v ?? 0).toLocaleString("en-IN") : "—"}</p>
              </div>
              {Icon && <Icon className="h-4 w-4 text-muted-foreground mt-1" aria-hidden="true" />}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* results */}
      <Card className="shadow-sm">
        <CardContent className="p-0">
          {!gate.fetched ? (
            <FetchPrompt onFetch={gate.fetch} testId={`${testId}-fetch-prompt`}
              subtitle="Pick a date range, Zone / Ward, who made the change, the role and the event type above, then click Fetch to load the log." />
          ) : error ? (
            <EmptyState title="Could not load the change log" subtitle={error}
              action={<Button variant="outline" onClick={load} data-testid={`${testId}-retry`}>Try again</Button>} />
          ) : loading && !data ? (
            <div className="p-4"><TableRowsSkeleton rows={8} cols={10} testId={`${testId}-skeleton`} /></div>
          ) : items.length === 0 ? (
            <EmptyState title="No mobile number events in this range"
              subtitle="Nothing was updated or verified for these filters. Widen the date range or clear a filter." />
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid={`${testId}-table`}>
                <TableHeader>
                  <TableRow>
                    <TableHead className="whitespace-nowrap">Date and Time</TableHead>
                    <TableHead>PIN</TableHead>
                    <TableHead>Owner Name</TableHead>
                    <TableHead className="whitespace-nowrap">Old Mobile Number</TableHead>
                    <TableHead className="whitespace-nowrap">New Mobile Number</TableHead>
                    <TableHead className="whitespace-nowrap">Changed By (Name and ID)</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead className="whitespace-nowrap">Receipt Number</TableHead>
                    <TableHead className="min-w-[220px]">Visiting Remarks</TableHead>
                    <TableHead className="whitespace-nowrap">Event Type</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((r) => (
                    <TableRow key={r.id} data-testid={`${testId}-row-${r.id}`} data-event={r.event_type}>
                      <TableCell className="text-xs whitespace-nowrap tabular-nums" data-testid={`${testId}-cell-time-${r.id}`}>
                        {fmtDateTime(r.at)}
                        {r.offline_recorded_at && <p className="text-[10px] text-muted-foreground">recorded offline · synced later</p>}
                      </TableCell>
                      <TableCell className="font-mono text-xs font-medium whitespace-nowrap" data-testid={`${testId}-cell-pin-${r.id}`}>
                        {propertyPath ? <a href={propertyPath(r)} className="text-primary hover:underline">{r.pin || "—"}</a> : (r.pin || "—")}
                        {(r.zone_name || r.ward_name) && <p className="text-[10px] text-muted-foreground font-sans">{[r.zone_name, r.ward_name].filter(Boolean).join(" › ")}</p>}
                      </TableCell>
                      <TableCell className="text-sm" data-testid={`${testId}-cell-owner-${r.id}`}>{r.owner_name || "—"}</TableCell>
                      <TableCell className="text-sm tabular-nums whitespace-nowrap" data-testid={`${testId}-cell-old-${r.id}`}>
                        {r.old_mobile ? fmtMobile(r.old_mobile) : <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="text-sm tabular-nums whitespace-nowrap font-medium" data-testid={`${testId}-cell-new-${r.id}`}>
                        {r.new_mobile ? fmtMobile(r.new_mobile) : "—"}
                        {r.event_type === "verified" && <p className="text-[10px] text-muted-foreground font-normal">confirmed correct</p>}
                      </TableCell>
                      <TableCell className="text-sm whitespace-nowrap" data-testid={`${testId}-cell-by-${r.id}`}>{r.by_label}</TableCell>
                      <TableCell className="text-xs whitespace-nowrap" data-testid={`${testId}-cell-role-${r.id}`}>{r.role_label}</TableCell>
                      <TableCell className="text-xs font-mono whitespace-nowrap" data-testid={`${testId}-cell-receipt-${r.id}`}>
                        {r.receipt_number ? (
                          <span className="inline-flex items-center gap-1"><Receipt className="h-3 w-3 text-muted-foreground" aria-hidden="true" /> {r.receipt_number}</span>
                        ) : <span className="text-muted-foreground font-sans">—</span>}
                      </TableCell>
                      <TableCell className="text-xs max-w-[320px]" data-testid={`${testId}-cell-remark-${r.id}`}>
                        {r.visit_remark ? (
                          <>
                            <p className="line-clamp-2 whitespace-normal break-words" title={r.visit_remark}>{r.visit_remark}</p>
                            {r.visit_outcome_label && <p className="text-[10px] text-muted-foreground mt-0.5">{r.visit_outcome_label}</p>}
                          </>
                        ) : <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell data-testid={`${testId}-cell-event-${r.id}`}><EventBadge type={r.event_type} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          {gate.fetched && data && data.total > 0 && (
            <div className="px-4 pb-4">
              <PaginationBar page={page} limit={limit} total={data.total} loading={loading}
                onPageChange={setPage} onLimitChange={(n) => { setLimit(n); setPage(1); }} testId={`${testId}-pagination`} />
            </div>
          )}
        </CardContent>
      </Card>
      {gate.fetched && data && (
        <p className="text-[11px] text-muted-foreground flex items-center gap-1.5" data-testid={`${testId}-footnote`}>
          <Search className="h-3 w-3" aria-hidden="true" />
          Receipt Number and Visiting Remarks are filled when the same staff member collected a payment or logged a visit remark on that property within ±{data.session_window_minutes || 30} minutes of the event. Dates and times in IST.
        </p>
      )}
    </div>
  );
}
